// Simple XOR encryption with salt for URL-safe payload
// This provides basic obfuscation for sensitive data

// Salt should be provided by the user or from environment variable (server-side only)
const SALT = process.env.REACT_APP_ENCRYPTION_SALT || '';

/**
 * XOR encryption/decryption with salt
 * @param {string} text - Text to encrypt/decrypt
 * @param {string} key - Encryption key (salt)
 * @returns {string} - Encrypted/decrypted text
 */
function xorCrypt(text, key) {
  let result = '';
  for (let i = 0; i < text.length; i++) {
    result += String.fromCharCode(text.charCodeAt(i) ^ key.charCodeAt(i % key.length));
  }
  return result;
}

/**
 * Encrypt data with salt and make it URL-safe
 * @param {Object} data - Data object to encrypt
 * @param {string} customSalt - Optional custom salt (defaults to SALT constant)
 * @returns {string} - Encrypted and URL-safe string
 */
export const encryptData = (data, customSalt = null) => {
  try {
    // Convert to JSON
    const jsonString = JSON.stringify(data);
    
    // XOR encrypt with salt
    const salt = customSalt || SALT;
    const encrypted = xorCrypt(jsonString, salt);
    
    // Base64 encode
    const base64 = btoa(unescape(encodeURIComponent(encrypted)));
    
    // Make URL-safe
    return base64
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  } catch (error) {
    console.error('Encryption error:', error);
    return null;
  }
};

/**
 * Decrypt URL-safe encrypted data
 * @param {string} encryptedString - Encrypted string from URL
 * @returns {Object} - Decrypted data object
 */
export const decryptData = (encryptedString) => {
  try {
    // Restore Base64 format
    let base64 = encryptedString
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    
    // Add padding if needed
    while (base64.length % 4) {
      base64 += '=';
    }
    
    // Base64 decode
    const encrypted = decodeURIComponent(escape(atob(base64)));
    
    // XOR decrypt with salt
    const jsonString = xorCrypt(encrypted, SALT);
    
    // Parse JSON
    return JSON.parse(jsonString);
  } catch (error) {
    console.error('Decryption error:', error);
    return null;
  }
};

/**
 * Generate encrypted document URL
 * @param {Object} data - Document data
 * @param {string} customSalt - Optional custom salt
 * @returns {string} - Full URL with encrypted payload
 */
export const generateDocumentUrl = (data, customSalt = null) => {
  const encrypted = encryptData(data, customSalt);
  if (!encrypted) return null;
  
  const baseUrl = window.location.origin;
  return `${baseUrl}/document?payload=${encrypted}`;
};

/**
 * Encrypt payload for external use (alias for encryptData)
 * @param {Object} data - Data object to encrypt
 * @param {string} customSalt - Optional custom salt
 * @returns {string} - Encrypted and URL-safe string
 */
export const encryptPayload = (data, customSalt = null) => {
  return encryptData(data, customSalt);
};

// Export for testing
export const _testXor = xorCrypt;